/**
 * Report generation progress stages.
 *
 * Interactive reports run as a single synchronous HTTP request (SQL + serialize).
 * The server does not currently emit per-query or row-batch progress, so UI stages
 * are either:
 *   1. Client-known phases (fetch → generate file → finalize) — real stages
 *   2. Elapsed-time status messages while waiting on a blocking request — UX only
 *
 * Percentages are only shown when a caller passes an explicit `percent` value.
 * Do not invent completion percentages from elapsed time.
 */

export const REPORT_PROGRESS_STAGES = Object.freeze({
  PREPARING: 'preparing',
  LOADING_DATA: 'loading_data',
  PROCESSING: 'processing',
  GENERATING_EXPORT: 'generating_export',
  FINALIZING: 'finalizing',
})

export const REPORT_PROGRESS_MESSAGES = Object.freeze({
  [REPORT_PROGRESS_STAGES.PREPARING]: 'Preparing Report...',
  [REPORT_PROGRESS_STAGES.LOADING_DATA]: 'Loading Report Data...',
  [REPORT_PROGRESS_STAGES.PROCESSING]: 'Processing Results...',
  [REPORT_PROGRESS_STAGES.GENERATING_EXPORT]: 'Generating Export...',
  [REPORT_PROGRESS_STAGES.FINALIZING]: 'Finalizing...',
})

/** Default elapsed-time message schedule for blocking preview/export waits (ms). */
export const REPORT_WAIT_STAGE_SCHEDULE = Object.freeze([
  { afterMs: 0, stage: REPORT_PROGRESS_STAGES.PREPARING },
  { afterMs: 2500, stage: REPORT_PROGRESS_STAGES.LOADING_DATA },
  { afterMs: 8000, stage: REPORT_PROGRESS_STAGES.PROCESSING },
  { afterMs: 20000, stage: REPORT_PROGRESS_STAGES.FINALIZING },
])

export const EXPORT_FORMAT_STAGE_LABELS = Object.freeze({
  excel: 'Generating Excel...',
  xlsx: 'Generating Excel...',
  pdf: 'Generating PDF...',
  csv: 'Generating CSV...',
})

export function getReportProgressMessage(
  stage,
  fallback = 'Generating Report...'
) {
  if (!stage) return fallback
  return REPORT_PROGRESS_MESSAGES[stage] || fallback
}

/**
 * Resolve which wait-stage message to show for an elapsed duration.
 * Used only when the caller has not set an explicit stage.
 */
export function resolveWaitStage(
  elapsedMs,
  schedule = REPORT_WAIT_STAGE_SCHEDULE
) {
  let stage = schedule[0]?.stage || REPORT_PROGRESS_STAGES.PREPARING
  for (const entry of schedule) {
    if (elapsedMs >= entry.afterMs) {
      stage = entry.stage
    }
  }
  return stage
}

/**
 * Normalize an optional percent for display.
 * Returns null when percent is unavailable or invalid so the UI stays indeterminate.
 */
export function normalizeProgressPercent(percent) {
  if (percent === null || percent === undefined || percent === '') return null
  const value = Number(percent)
  if (!Number.isFinite(value)) return null
  return Math.min(100, Math.max(0, Math.round(value)))
}

export function isAbortError(error) {
  if (!error) return false
  if (error.name === 'CanceledError' || error.name === 'AbortError') return true
  if (error.code === 'ERR_CANCELED') return true
  const message = String(error.message || '').toLowerCase()
  return (
    message.includes('canceled') ||
    message.includes('cancelled') ||
    message.includes('aborted')
  )
}
